import classNames from 'classnames'
import _ from 'lodash'
import PT from 'prop-types'
import React from 'react'
import { Layout as ILayout, Layouts as ILayouts, Responsive, WidthProvider } from 'react-grid-layout'
import styled, { keyframes } from 'styled-components'
import DashboardConfig from 'config/DashboardConfig'
import { DashboardGridProps, LayoutTab, Widget } from 'index.d'
import {
  BreakpointPropType,
  DroppingItemPropType,
  LabelsPropType,
  LayoutTabsPropType,
  WidgetsPropType
} from 'index.pt'
import DashboardTabs from './DashboardTabs'
import WidgetContainer from './WidgetContainer'

const ResponsiveReactGridLayout = WidthProvider(Responsive)
const redpulse = keyframes`
  0% {
     opacity: 0;
     background-color: var(--navds-semantic-color-canvas-background);
  }
  50% {
    opacity: 0.25;
    background-color: var(--navds-semantic-color-interaction-primary-interaction-hover);
  }
  100% {
    background-color: var(--navds-semantic-color-canvas-background);
    opacity: 0;
  }
`
export const DashboardGridDiv = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--navds-semantic-color-canvas-background);
  &.editMode {
    .react-grid-layout {  
       background-color: var(--navds-semantic-color-canvas-background) !important;
       border-left: 1px dashed var(--navds-semantic-color-border);
       border-right: 1px dashed var(--navds-semantic-color-border);
       border-bottom: 1px dashed var(--navds-semantic-color-border);
    }
    .react-grid-item {  
       background-color: var(--navds-semantic-color-component-background-alternate);
       border: 1px dashed grey;
       &.react-grid-placeholder {
         animation: ${redpulse} 1s ease-in-out infinite alternate; 
       }
     }
  }
  .react-grid-layout {
     position: relative;
     flex: 1 0 auto;
     background-color: var(--navds-semantic-color-canvas-background) !important;
  }  
  .react-grid-item {
    box-sizing: border-box;
  }
  .react-grid-item.resizing {
    opacity: 0.9;
  }
  .react-grid-item.cssTransforms {
    transition-property: transform;/*, height, width;*/
  }
  .react-grid-dragHandleExample {
    cursor: move; /* fallback if grab cursor is unsupported */
    cursor: grab;
  }
`

export const DashboardGrid: React.FC<DashboardGridProps> = (props: DashboardGridProps): JSX.Element => {
  const { currentTab, currentBreakpoint, draggedWidget, droppingItem, editMode, highContrast, labels } = props
  const { layouts, myWidgets, onBreakpointChange, onLayoutChange, onWidgetDrop, onWidgetFullFocus } = props
  const { onWidgetRestoreFocus, onWidgetUpdate, onWidgetResize, onWidgetDelete, onTabAdd, onTabChange, onTabRename } = props
  const { onWidgetDragStart, onWidgetDragStop, onTabDelete, onTabMove, rowHeight, widgets } = props

  if (_.isEmpty(layouts) || _.isNil(currentTab)) {
    return <div />
  }

  const layout: LayoutTab = _.find(layouts, {key: currentTab}) as LayoutTab

  return (

    <DashboardGridDiv
      id='dashboardGrid'
      className={classNames({
        canDrop: false,
        editMode: editMode
      })}
    >
      <DashboardTabs
        editMode={editMode}
        labels={labels}
        onTabAdd={onTabAdd}
        onTabDelete={onTabDelete}
        onTabChange={onTabChange}
        onTabRename={onTabRename}
        onTabMove={onTabMove}
        currentTab={currentTab}
        layouts={layouts}
      />
      <ResponsiveReactGridLayout
        {...props}
        breakpoints={DashboardConfig.breakpoints as unknown as {[P: string]: number}}
        droppingItem={droppingItem}
        autoSize
        margin={DashboardConfig.margin as [number, number]}
        containerPadding={DashboardConfig.containerPadding as [number, number]}
        isDraggable={editMode}
        isResizable={editMode}
        layouts={layout.body as unknown as ILayouts}
        onBreakpointChange={onBreakpointChange}
        onLayoutChange={onLayoutChange as unknown as (currentLayout: ILayout[], allLayouts: ILayouts) => void}
        onDragStart={onWidgetDragStart}
        onDragStop={onWidgetDragStop}
        measureBeforeMount
        useCSSTransforms
        preventCollision={false}
        isDroppable
        onDrop={onWidgetDrop}
        draggableHandle='.draggableHandle'
      >
        {_.map(layout.body[currentBreakpoint], (layout) => {
          const widget: Widget | undefined = _.find(widgets, { i: layout.i })
          const isDragging: boolean = (draggedWidget && widget && draggedWidget.i === widget.i) || false
          return widget
            ? (
              <div id={'widget-' + layout.i} key={layout.i}>
                <WidgetContainer
                  widget={widget}
                  isDragging={isDragging}
                  currentBreakpoint={currentBreakpoint}
                  editMode={editMode}
                  highContrast={highContrast}
                  layout={layout}
                  labels={labels}
                  onWidgetResize={onWidgetResize}
                  onWidgetUpdate={onWidgetUpdate}
                  onWidgetDelete={onWidgetDelete}
                  onWidgetFullFocus={onWidgetFullFocus}
                  onWidgetRestoreFocus={onWidgetRestoreFocus}
                  rowHeight={rowHeight}
                  myWidgets={myWidgets}
                />
              </div>
              )
            : null
        }).filter(w => !_.isNil(w))}
      </ResponsiveReactGridLayout>
    </DashboardGridDiv>
  )
}

DashboardGrid.propTypes = {
  currentBreakpoint: BreakpointPropType.isRequired,
  currentTab: PT.string.isRequired,
  droppingItem: DroppingItemPropType.isRequired,
  editMode: PT.bool.isRequired,
  labels: LabelsPropType.isRequired,
  layouts: LayoutTabsPropType.isRequired,
  onBreakpointChange: PT.func.isRequired,
  onLayoutChange: PT.func.isRequired,
  onWidgetUpdate: PT.func.isRequired,
  onWidgetDrop: PT.func.isRequired,
  onWidgetResize: PT.func.isRequired,
  onWidgetDelete: PT.func.isRequired,
  onWidgetFullFocus: PT.func.isRequired,
  onWidgetRestoreFocus: PT.func.isRequired,
  onTabAdd: PT.func.isRequired,
  onTabDelete: PT.func.isRequired,
  onTabChange: PT.func.isRequired,
  onTabRename: PT.func.isRequired,
  onTabMove: PT.func.isRequired,
  rowHeight: PT.number.isRequired,
  widgets: WidgetsPropType.isRequired
}

DashboardGrid.defaultProps = DashboardConfig

export default DashboardGrid
